import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Plus, ShoppingCart, Upload, X } from "lucide-react";
import { api, errMsg, eur } from "@/lib/api";
import { PrinterPicker } from "./warehouse/PrinterPicker";
import { StateBadge, StorePicker, sendToCassa, useStore } from "./warehouse/whApi";
import { CatalogImport } from "./warehouse/CatalogImport";

function ReversePanel({ row, onClose }) {
  const [list, setList] = useState(null);
  useEffect(() => { setList(null); api.get(`/admin/compat/article/${encodeURIComponent(row.code)}`).then((r) => setList(r.data.printers)).catch(() => setList([])); }, [row.code]);
  return (
    <aside className="rounded-xl border-2 border-brand-navy bg-white p-4 space-y-3 lg:sticky lg:top-[110px]" data-testid="compat-reverse-panel">
      <div className="flex items-start justify-between gap-2">
        <div><div className="font-mono font-extrabold text-lg">{row.code}</div><p className="text-sm text-slate-600">{row.description}</p></div>
        <button data-testid="compat-reverse-close" onClick={onClose} className="press h-8 w-8 grid place-items-center rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
      </div>
      <div className="text-xs font-mono uppercase text-slate-500">Stampanti compatibili ({list?.length ?? "…"})</div>
      <div className="flex flex-wrap gap-1.5 max-h-[50vh] overflow-y-auto">{list?.map((p) => <span key={p.id} className="text-[13px] px-2 py-0.5 rounded bg-slate-100">{p.label}</span>)}</div>
    </aside>
  );
}

const Check = ({ id, checked, onChange, children }) => (
  <label className="flex items-center gap-1.5 text-sm"><input type="checkbox" data-testid={id} checked={checked} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-brand-navy" />{children}</label>
);

export default function AdminCompat() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const { stores, store, setStore } = useStore();
  const [brand, setBrand] = useState(null);
  const [printer, setPrinter] = useState(null);
  const [q, setQ] = useState("");
  const [f, setF] = useState({ only_stock: false, exclude_disc: true, kind: "" });
  const [res, setRes] = useState({ total: 0, items: [], catalog_total: 0 });
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState(null);
  const [imp, setImp] = useState(false);
  useEffect(() => { const pid = sp.get("printer"); if (pid) api.get(`/admin/printers/${pid}`).then((r) => setPrinter(r.data)).catch(() => {}); }, [sp]);
  const load = () => {
    setLoading(true);
    const params = { printer_id: printer?.id || "", brand: printer ? "" : brand?.b || "", q: q.trim().length >= 2 ? q : "", store, ...f };
    return api.get("/admin/compat/search", { params }).then((r) => setRes(r.data)).catch((e) => toast.error(errMsg(e))).finally(() => setLoading(false));
  };
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [printer, brand, q, f, store]); // eslint-disable-line react-hooks/exhaustive-deps
  const add = async (r) => {
    try { await api.post("/admin/stock", { code: r.code, store }); toast.success("Articolo aggiunto al magazzino"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const sell = (r) => { sendToCassa(r); toast.success(`${r.code} pronto in Cassa`); nav("/admin/cassa"); };
  return (
    <div className="space-y-5" data-testid="admin-compat-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-4xl font-extrabold uppercase">Compatibilità stampanti</h1>
          <p className="text-sm text-slate-600">Trova le cartucce compatibili e aggiungile al magazzino · Totale catalogo: <b data-testid="compat-catalog-total">{res.catalog_total}</b> articoli</p>
        </div>
        <div className="flex gap-2">
          <StorePicker stores={stores} store={store} setStore={setStore} />
          <button data-testid="compat-import-btn" onClick={() => setImp(true)} className="press h-10 px-4 rounded-md bg-brand-yellow text-brand-navy text-sm font-bold flex items-center gap-2"><Upload className="w-4 h-4" /> Import catalogo fornitore</button>
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-2">
        <PrinterPicker brand={brand} setBrand={setBrand} printer={printer} setPrinter={setPrinter} />
        <input data-testid="compat-text-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Codice, descrizione o barcode (min 2)" className="h-11 px-3 rounded-md border border-slate-300 bg-white text-sm" />
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <Check id="compat-only-stock" checked={f.only_stock} onChange={(v) => setF({ ...f, only_stock: v })}>Solo disponibili in magazzino</Check>
        <Check id="compat-exclude-disc" checked={f.exclude_disc} onChange={(v) => setF({ ...f, exclude_disc: v })}>Escludi non più disponibili</Check>
        <Check id="compat-only-orig" checked={f.kind === "orig"} onChange={(v) => setF({ ...f, kind: v ? "orig" : "" })}>Solo originali</Check>
        <Check id="compat-only-compat" checked={f.kind === "compat"} onChange={(v) => setF({ ...f, kind: v ? "compat" : "" })}>Solo compatibili</Check>
        {(printer || brand) && <button data-testid="compat-clear" onClick={() => { setPrinter(null); setBrand(null); }} className="text-sm text-rose-600 font-semibold">Azzera stampante</button>}
      </div>
      <div className={`grid gap-4 ${sel ? "lg:grid-cols-[1fr_340px]" : ""}`}>
        <div className="rounded-xl border bg-white overflow-x-auto">
          <div className="px-3 py-2 text-[13px] font-mono text-slate-500 bg-slate-50 border-b flex items-center gap-2" data-testid="compat-results-count">{loading && <Loader2 className="w-3 h-3 animate-spin" />} {res.total} risultati</div>
          <table className="w-full text-sm min-w-[760px]">
            <thead className="text-left text-[11px] uppercase font-mono text-slate-500 border-b"><tr><th className="p-2">Codice</th><th className="p-2">Descrizione</th><th className="p-2">Barcode</th><th className="p-2 text-right">Prezzo</th><th className="p-2">Listino</th><th className="p-2 text-right">Giac.</th><th className="p-2">Stato</th><th className="p-2" /></tr></thead>
            <tbody>
              {res.items.map((r) => (
                <tr key={r.code} onClick={() => setSel(r)} className={`border-b cursor-pointer odd:bg-slate-50/60 hover:bg-brand-yellow/10 ${sel?.code === r.code ? "bg-brand-yellow/20" : ""}`} data-testid={`compat-row-${r.code}`}>
                  <td className="p-2 font-mono font-extrabold text-[15px] whitespace-nowrap">{r.code}</td>
                  <td className="p-2 text-[13px]">{r.description}{r.discontinued && <span className="ml-1 text-[10px] font-bold px-1 rounded bg-slate-400 text-white">FUORI PROD.</span>}</td>
                  <td className="p-2 font-mono text-xs">{r.barcode}</td>
                  <td className="p-2 text-right font-bold whitespace-nowrap">{eur(r.price)}</td>
                  <td className="p-2 text-xs">{r.supplier}</td>
                  <td className="p-2 text-right font-mono">{r.qty ?? "—"}</td>
                  <td className="p-2"><StateBadge s={r.state} testid={`compat-state-${r.code}`} /></td>
                  <td className="p-2" onClick={(e) => e.stopPropagation()}>
                    {r.in_stock
                      ? <button data-testid={`compat-sell-${r.code}`} onClick={() => sell(r)} className="press h-8 px-3 rounded bg-brand-navy text-white text-xs font-semibold flex items-center gap-1"><ShoppingCart className="w-3.5 h-3.5" /> Vendi</button>
                      : <button data-testid={`compat-add-${r.code}`} onClick={() => add(r)} className="press h-8 px-3 rounded border border-brand-navy text-xs font-semibold flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Aggiungi</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!res.items.length && !loading && <p className="p-5 text-sm text-slate-500">Scegli marca e modello oppure scrivi un codice.</p>}
        </div>
        {sel && <ReversePanel row={sel} onClose={() => setSel(null)} />}
      </div>
      <CatalogImport open={imp} onClose={() => setImp(false)} onDone={load} />
    </div>
  );
}
